import PropTypes from 'prop-types';
import { useContext, useMemo, useState } from 'react';
import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming';
import BlockingNavigationDialog from '../../../../components/BlockingNavigationDialog';
import { useUserSessionContext } from '../../../../components/UserSessionContextWrapper';
import { showErrorNotificationFromResponse } from '../../../../services/errorUtils';
import GeneralInfoPropType from '../../GeneralInfoPropType';
import { updateGeneralInfo } from '../../myAccountApi';
import { isMobile } from '../../../../services/DeviceInformation';
import GeneralEditDesktop from './GeneralEditDesktop';
import GeneralEditMobile from './GeneralEditMobile';
import GeneralEditContext from './GeneralEditContext';
import ResponseStatus from '../../../../constants/ResponseStatus';
import useCountries from '../../../../components/PhoneInputField/useCountries';
import useNetworkErrorNotification from '../../../../services/useNetworkErrorNotification';
import LoadingScreenContext from '../../../../services/LoadingScreenContext';
import AuthenticatedRouteLanguageContext from '../../../../services/AuthenticatedRouteLanguageContext';
import trimToMaxLength from '../../../../services/trimToMaxLength';
import { stringIsEmpty } from '../StringUtils';
import PhoneUtils from '../PhoneUtils';
import { transformGeneralInfo } from '../GeneralInfoUtils';

const propTypes = {
    /**
     * Translated field labels for edit mode
     */
    fieldLabels: PropTypes.shape({
        cancelLabel: PropTypes.string,
        countryCodeLabel: PropTypes.string,
        firstNameLabel: PropTypes.string,
        languageLabel: PropTypes.string,
        lastNameLabel: PropTypes.string,
        localeLabel: PropTypes.string,
        mobilePhoneLabel: PropTypes.string,
        phoneNumberErrorLabel: PropTypes.string,
        saveLabel: PropTypes.string,
        timezoneLabel: PropTypes.string,
    }),
    generalInfo: GeneralInfoPropType.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    /**
     * Options metadata for languages, locales and timezones.
     */
    options: PropTypes.exact({
        languages: PropTypes.shape(),
        locales: PropTypes.shape(),
        timezones: PropTypes.arrayOf(
            PropTypes.exact({
                label: PropTypes.string,
                value: PropTypes.string,
            }),
        ),
    }),
    setLocaleOptions: PropTypes.func.isRequired,
    /**
     * Temporary flag to modify UI behaviors. Remove when feature flag is turned off.
     */
    shouldDecoupleLanguageLocale: PropTypes.bool,
};

const GeneralEdit = ({
    onCancel,
    fieldLabels,
    generalInfo,
    onSave,
    options,
    setLocaleOptions,
    shouldDecoupleLanguageLocale,
}) => {
    const { showNotification } = useNetworkErrorNotification();
    const { handleResponseSessionErrors } = useUserSessionContext();

    const { phoneNumberErrorLabel } = fieldLabels;
    const countries = useCountries();
    const [firstName, setFirstName] = useState(generalInfo.firstName);
    const [lastName, setLastName] = useState(generalInfo.lastName);
    const [language, setLanguage] = useState(generalInfo.language);
    const [locale, setLocale] = useState(generalInfo.locale);
    const [timezone, setTimezone] = useState(generalInfo.timezone);
    const [phoneValue, setPhoneValue] = useState(generalInfo.phone);
    const [phoneCountryCode, setPhoneCountryCode] = useState(generalInfo.phoneCountryCode);
    const [countryCodeError, setCountryCodeError] = useState();
    const [phoneError, setPhoneError] = useState();
    const { setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage, setMessageLanguage } = useContext(AuthenticatedRouteLanguageContext);

    const handlePhoneValueChange = (newValue) => {
        // E.164 states that phone numbers (including country code) must be up to 15 digits.
        if (newValue === '' || PhoneUtils.isValidPhoneNumberLength(phoneCountryCode, newValue)) {
            setPhoneValue(newValue);
            setPhoneError(undefined);
            setCountryCodeError(undefined);
        }
    };

    const handleCountryCodeChange = (newValue) => {
        setPhoneCountryCode(newValue);
        setPhoneValue(undefined);
        setPhoneError(undefined);
    };

    const handleLanguageChange = async (lang) => {
        if (shouldDecoupleLanguageLocale) {
            setLanguage(lang);
        } else {
            await setLocaleOptions(lang);
            setLanguage(lang);
            setLocale('');
        }
    };

    const handleLocaleChange = (newValue) => {
        setLocale(newValue);
    };

    const handleTimezoneChange = (newValue) => {
        setTimezone(newValue);
    };

    const setInlineErrors = () => {
        let atLeastOneError = false;

        if (stringIsEmpty(phoneValue) && !stringIsEmpty(phoneCountryCode)) {
            setPhoneError(phoneNumberErrorLabel);
            atLeastOneError = true;
        }

        return atLeastOneError;
    };

    const handleCancel = async () => {
        if (!shouldDecoupleLanguageLocale) {
            await setLocaleOptions(generalInfo.language);
        }
        setLanguage(generalInfo.language);
        setLocale(generalInfo.locale);
        onCancel();
    };

    const handleSave = async () => {
        if (setInlineErrors()) {
            return;
        }

        const trimmedFirstName = firstName?.trim();
        const trimmedLastName = lastName?.trim();
        setFirstName(trimmedFirstName);
        setLastName(trimmedLastName);

        setLoading(true);
        const response = await updateGeneralInfo(
            trimmedFirstName,
            trimmedLastName,
            phoneCountryCode,
            phoneValue,
            language,
            locale,
            timezone,
        );
        setLoading(false);

        if (handleResponseSessionErrors(response)) {
            return;
        }

        switch (response?.responseStatus) {
            case ResponseStatus.SUCCESS:
                setMessageLanguage(language);
                onSave(transformGeneralInfo(response?.data));
                break;
            case ResponseStatus.FAILURE: {
                const i18nOptions = { language: messageLanguage };
                await showErrorNotificationFromResponse(response, i18nOptions);
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const anyFieldChanged = useMemo(
        () =>
            firstName !== generalInfo.firstName ||
            lastName !== generalInfo.lastName ||
            language !== generalInfo.language ||
            locale !== generalInfo.locale ||
            timezone !== generalInfo.timezone ||
            phoneValue !== generalInfo.phone ||
            phoneCountryCode !== generalInfo.phoneCountryCode,
        [
            firstName,
            generalInfo.firstName,
            lastName,
            generalInfo.lastName,
            language,
            generalInfo.language,
            locale,
            generalInfo.locale,
            timezone,
            generalInfo.timezone,
            phoneValue,
            generalInfo.phone,
            phoneCountryCode,
            generalInfo.phoneCountryCode,
        ],
    );

    const handleChangeFirstName = (newFirstName) => {
        setFirstName(trimToMaxLength(newFirstName));
    };

    const handleChangeLastName = (newLastName) => {
        setLastName(trimToMaxLength(newLastName));
    };

    const saveButtonDisabled = ![firstName, lastName, language, locale, timezone].every(Boolean);
    const generalEditTheme = {
        veevaIdErrorLabelBufferHeight: '2.333rem',
        veevaIdDisplayFieldLabelBottomMargin: '0.667rem',
    };

    return (
        <GeneralEditContext.Provider
            value={{
                fieldLabels,
                countryCode: phoneCountryCode,
                countryCodeError,
                countries,
                firstName,
                lastName,
                language,
                locale,
                timezone,
                options,
                onCancel: handleCancel,
                onCountryCodeChange: handleCountryCodeChange,
                onLanguageChange: handleLanguageChange,
                onLocaleChange: handleLocaleChange,
                onTimezoneChange: handleTimezoneChange,
                onFirstNameChange: handleChangeFirstName,
                onLastNameChange: handleChangeLastName,
                onPhoneChange: handlePhoneValueChange,
                onSave: handleSave,
                phone: phoneValue,
                phoneDisabled: stringIsEmpty(phoneCountryCode),
                phoneError,
                saveButtonDisabled,
            }}
        >
            <BlockingNavigationDialog when={anyFieldChanged} />
            <EmotionThemeProvider theme={generalEditTheme}>
                {isMobile() ? <GeneralEditMobile /> : <GeneralEditDesktop />}
            </EmotionThemeProvider>
        </GeneralEditContext.Provider>
    );
};

GeneralEdit.propTypes = propTypes;

export default GeneralEdit;
